import { useEffect, useRef, useState } from 'react';

import { useUiStore } from '@/store/uiStore';
import { useLogout, useMe } from '@/hooks/useAuth';
import styles from './Topbar.module.css';

// Local extension of the generated MeResponse — the live API returns
// `firstName` / `lastName` / `displayName` for Google-signed-in users
// but orval's generated type is stale (swagger source not on disk).
interface MeWithName {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" />
    </svg>
  );
}

function BrandMark() {
  return (
    <img
      src="/logo.PNG"
      alt=""
      width={24}
      height={24}
      decoding="async"
      style={{ borderRadius: 6 }}
    />
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M9 4H5a1 1 0 00-1 1v10a1 1 0 001 1h4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 7l3 3-3 3M16 10H9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined,
): string {
  // Prefer first + last initials when the user's name is on file. Fall
  // back to a heuristic over the email local-part so existing
  // password-only accounts still render something sensible.
  if (firstName || lastName) {
    const f = (firstName ?? '').trim().charAt(0).toUpperCase();
    const l = (lastName ?? '').trim().charAt(0).toUpperCase();
    if (f && l) return f + l;
    if (f) return f;
    if (l) return l;
  }
  if (!email) return '·';
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return email.charAt(0).toUpperCase();
  if (parts.length === 1) {
    const first = parts[0] ?? '';
    return first.slice(0, 2).toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
}

function getEnvLabel(): { label: string; tone: 'dev' | 'prod' } | null {
  if (import.meta.env.DEV) return { label: 'Dev', tone: 'dev' };
  // Could expand later — staging vs prod from env vars.
  return null;
}

export const Topbar = () => {
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const logout = useLogout();
  const { data: user } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const env = getEnvLabel();
  const userWithName = user as (typeof user & MeWithName) | undefined;
  const firstName = userWithName?.firstName ?? null;
  const lastName = userWithName?.lastName ?? null;
  // Build a display name in priority order:
  //   1. server-derived `displayName` (covers "First Last", or the
  //      email local-part for non-Google accounts)
  //   2. first + last assembled here
  //   3. email local-part
  //   4. email
  //   5. null (user record not loaded yet)
  function deriveDisplayName(): string | null {
    const fromServer = userWithName?.displayName?.trim();
    if (fromServer !== undefined && fromServer.length > 0) return fromServer;
    const fromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fromParts.length > 0) return fromParts;
    const email = user?.email;
    if (email === undefined || email.length === 0) return null;
    const local = email.split('@')[0];
    if (local !== undefined && local.length > 0) return local;
    return email;
  }
  const displayName = deriveDisplayName();
  const initials = getInitials(firstName, lastName, user?.email);

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
          title="Toggle sidebar"
        >
          <HamburgerIcon />
        </button>
        <span className={styles.brand} aria-label="LIFTIQ Admin">
          <BrandMark />
          <span className={styles.brandText}>
            LIFTIQ <span className={styles.brandSuffix}>Admin</span>
          </span>
        </span>
        {env && (
          <span className={`${styles.envBadge} ${styles[`env${env.tone}`] ?? ''}`}>
            {env.label}
          </span>
        )}
      </div>

      <div className={styles.right} ref={menuRef}>
        <button
          type="button"
          className={`${styles.userBtn} ${menuOpen ? styles.userBtnOpen : ''}`}
          onClick={() => {
            setMenuOpen((s) => !s);
          }}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span className={styles.avatar} aria-hidden="true">
            {initials}
          </span>
          {displayName !== null && <span className={styles.userEmail}>{displayName}</span>}
          <span className={styles.chev} aria-hidden="true">
            <ChevronDownIcon />
          </span>
        </button>

        {menuOpen && (
          <div className={styles.menu} role="menu">
            {user?.email && (
              <div className={styles.menuHeader}>
                <span className={styles.menuAvatar} aria-hidden="true">
                  {initials}
                </span>
                <div className={styles.menuIdentity}>
                  <span className={styles.menuName}>{displayName ?? user.email.split('@')[0]}</span>
                  <span className={styles.menuEmail}>{user.email}</span>
                </div>
              </div>
            )}
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
            >
              <LogoutIcon />
              Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};
